// First-party account creation: POST /api/v1/auth/signup. A 201 carries the same body as
// POST /api/v1/auth/login, so the session is stored exactly like a password sign-in.
import axios from 'axios';
import config from '../config';
import {fromLoginResponse, saveSession} from './legacy';

const SIGNUP_PATH = '/api/v1/auth/signup';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Same email check as the signup form (shared with password recovery). */
export const isEmailAddress = (value) => typeof value === 'string' && EMAIL_RE.test(value);

export const SIGNUP_FIELDS = Object.freeze([
  'first_name',
  'last_name',
  'email',
  'phone',
  'password',
  'confirm_password',
]);

export const MESSAGES = Object.freeze({
  first_name: 'Enter your first name.',
  last_name: 'Enter your last name.',
  email: 'Enter your email address.',
  emailFormat: 'Enter a valid email address, like name@example.com.',
  password: 'Enter a password.',
  passwordRule: 'Password must be at least 8 characters and include a letter and a number.',
  confirm_password: 'Confirm your password.',
  confirmMismatch: 'Passwords do not match.',
  terms: 'Please agree to the Terms of Service and the Privacy Policy.',
  conflict: 'An account with this email already exists',
  invalid: 'Some details were not accepted. Please check the form.',
  generic: 'Sign-up failed. Please try again.',
});

/** Rejection value of `signup`: a user-facing `message` plus `{field: message}`. */
export class SignupError extends Error {
  constructor(message, fieldErrors = {}) {
    super(message);
    this.name = 'SignupError';
    this.fieldErrors = fieldErrors;
  }
}

export const passwordIsStrong = (password) =>
  typeof password === 'string' && password.length >= 8 && /[A-Za-z]/.test(password) && /\d/.test(password);

/**
 * Client-side checks. `values` holds the form strings plus `accept_terms` (boolean).
 * Returns `{field: message}`; empty when the form may be submitted. Keys follow form order.
 */
export const validateSignup = (values) => {
  const v = values || {};
  const errors = {};
  const text = (k) => (typeof v[k] === 'string' ? v[k].trim() : '');
  if (!text('first_name')) errors.first_name = MESSAGES.first_name;
  if (!text('last_name')) errors.last_name = MESSAGES.last_name;
  if (!text('email')) errors.email = MESSAGES.email;
  else if (!EMAIL_RE.test(text('email'))) errors.email = MESSAGES.emailFormat;
  const password = typeof v.password === 'string' ? v.password : '';
  if (!password) errors.password = MESSAGES.password;
  else if (!passwordIsStrong(password)) errors.password = MESSAGES.passwordRule;
  const confirm = typeof v.confirm_password === 'string' ? v.confirm_password : '';
  if (!confirm) errors.confirm_password = MESSAGES.confirm_password;
  else if (password && confirm !== password) errors.confirm_password = MESSAGES.confirmMismatch;
  if (v.accept_terms !== true) errors.accept_terms = MESSAGES.terms;
  return errors;
};

/** Form values -> request body. `phone` is omitted when blank; the password is sent as typed. */
export const toSignupRequest = (values) => {
  const body = {
    first_name: values.first_name.trim(),
    last_name: values.last_name.trim(),
    email: values.email.trim(),
    password: values.password,
    accept_terms: true,
  };
  const phone = typeof values.phone === 'string' ? values.phone.trim() : '';
  if (phone) body.phone = phone;
  return body;
};

export const newIdempotencyKey = () => {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

/** API field names (snake_case, possibly dotted/pointer style) -> form field names. */
const toFormField = (field) => {
  if (typeof field !== 'string') return null;
  const name = field.replace(/^\/|^body\./, '').replace(/\//g, '.');
  return SIGNUP_FIELDS.includes(name) || name === 'accept_terms' ? name : null;
};

/**
 * A failed signup request -> `{message, fieldErrors}` for the form. Never echoes anything but
 * the server's validation `detail`/field messages, and only for 422.
 */
export const signupErrorFromResponse = (err) => {
  const res = err && err.response;
  const status = res && res.status;
  if (status === 409) return {message: MESSAGES.conflict, fieldErrors: {email: MESSAGES.conflict}};
  if (status === 422) {
    const problem = (res.data && typeof res.data === 'object' && res.data) || {};
    const fieldErrors = {};
    const lines = [];
    for (const e of Array.isArray(problem.errors) ? problem.errors : []) {
      if (!e || typeof e.message !== 'string') continue;
      const field = toFormField(e.field);
      if (field && !fieldErrors[field]) fieldErrors[field] = e.message;
      lines.push(e.message);
    }
    const detail = typeof problem.detail === 'string' && problem.detail ? problem.detail : MESSAGES.invalid;
    return {message: [detail, ...lines].join(' '), fieldErrors};
  }
  return {message: MESSAGES.generic, fieldErrors: {}};
};

/**
 * Creates the account and stores the session (same storage as password sign-in).
 * Resolves with the session; rejects with a SignupError.
 */
export const signup = async (values, {idempotencyKey = newIdempotencyKey(), http = axios} = {}) => {
  let res;
  try {
    res = await http.post(`${config.apiBaseUrl}${SIGNUP_PATH}`, toSignupRequest(values), {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'Idempotency-Key': idempotencyKey,
      },
    });
  } catch (err) {
    const {message, fieldErrors} = signupErrorFromResponse(err);
    const failure = new SignupError(message, fieldErrors);
    failure.status = err && err.response ? err.response.status : undefined;
    throw failure;
  }
  let session;
  try {
    session = fromLoginResponse(res.data);
  } catch {
    throw new SignupError(MESSAGES.generic);
  }
  saveSession(session);
  return session;
};
